/**
 * locate.ts — the one place that answers "where do flows live, and what are
 * they called".
 *
 * It sits in the engine, next to `flowFileCandidates` / `loadAllFlowEntries`,
 * because both sides of the question have to agree: the CLI's *listing*
 * surfaces (`flow list`, completions) and the *resolution* surfaces that live
 * in this tree (`resume`, sub-flow `run.flow` targets, `serve`). They used to
 * hold two hand-maintained candidate lists, and only one of them ever grew.
 */

import fs from "node:fs";
import path from "node:path";
import { aiResourceRoots, assetSearchDirs, detectAiResources } from "@skaile/workspaces/core";
import { flowFileCandidates, loadAllFlowEntries, loadFlow } from "./loader.js";

/** How far up from the project the `ai-assets/` walk looks before giving up. */
const AI_ASSETS_WALK_LEVELS = 6;

/**
 * Roots that only the *resolution* surfaces prepend: the project's own
 * `ai-assets/` tree, so a workspace can shadow a built-in flow.
 *
 * Returns at most one root — the nearest `ai-assets/` at or above `projectDir`.
 * Listing deliberately does **not** include it; see `cli/src/flow-discovery.ts`.
 *
 * @docLink packages/flow-engine/concepts#flow-loading
 */
export function projectAiAssetsRoots(projectDir: string): string[] {
  const found = detectAiResources(projectDir, AI_ASSETS_WALK_LEVELS);
  return found ? [found] : [];
}

/**
 * Every root a flow lookup walks, in priority order: the project's own
 * `ai-assets/` tree first, then every shared content root.
 *
 * `aiResourceRoots(projectDir)` is a **subset** of this — that is the documented
 * relationship between "a flow `flow list` shows" and "a flow `resume` /
 * sub-flow targets / `serve` can find", and the reason both go through here.
 *
 * The authored `ai-assets/` tree leading is deliberate and is what `skaile run`
 * has always done: a workspace must be able to shadow a built-in flow. It is the
 * opposite of {@link aiResourceRoots}'s own ordering, where the deployed
 * `.skaile/` copy leads because `deployAll` applies `patches:` to it — so for a
 * flow id present in *both*, resolution takes the authored source. Resume must
 * execute the bytes `run` executed; that agreement is the point of this module.
 *
 * @param projectDir - Project whose `.skaile/` and `ai-assets/` roots to
 *   include (default: `process.cwd()`).
 * @docLink packages/flow-engine/concepts#flow-loading
 */
export function flowLookupRoots(projectDir?: string): string[] {
  const dir = projectDir ?? process.cwd();
  return [...new Set([...projectAiAssetsRoots(dir), ...aiResourceRoots(dir)])];
}

/**
 * Why a conventional candidate that exists on disk was still not accepted —
 * the difference between "no such flow" and "that file is right there but
 * declares something else", which a bare "not found" used to hide.
 *
 * @docLink packages/flow-engine/concepts#flow-loading
 */
export interface FlowLookupMiss {
  path: string;
  reason: "unparseable" | "id-mismatch";
  declaredId?: string;
  error?: string;
}

/** `candidate` resolves inside `dir` rather than escaping it via `..`. */
function contained(dir: string, candidate: string): boolean {
  const relative = path.relative(path.resolve(dir), path.resolve(candidate));
  return !relative.startsWith("..") && !path.isAbsolute(relative);
}

/**
 * Locate the file of the flow whose **declared id** is `flowId`, across `roots`.
 *
 * Per root: the conventional paths first (both content-root layouts via
 * `assetSearchDirs`, both flow layouts and all four extensions via
 * `flowFileCandidates`), then the full enumeration `loadAllFlowEntries` — the
 * same one `discoverFlows` lists from, which is what keeps listing and
 * resolution agreeing by construction. The fallback stays *inside* the loop so
 * an earlier root always wins over a later one.
 *
 * A candidate that resolves outside the search dir it was built from is
 * **skipped, not thrown**: this is on the `skaile run "<free-text prompt>"`
 * path, where a miss is normal and must fall through to single-shot mode.
 *
 * @param opts.onMiss - Called for a conventional candidate that exists but was
 *   rejected. Discovery warnings are suppressed regardless.
 * @docLink packages/flow-engine/concepts#flow-loading
 */
export function findFlowFileInRoots(
  flowId: string,
  roots: string[],
  opts: { onMiss?: (miss: FlowLookupMiss) => void } = {},
): string | undefined {
  for (const root of roots) {
    for (const { dir } of assetSearchDirs(root, "flows")) {
      for (const candidate of flowFileCandidates(dir, flowId)) {
        if (!contained(dir, candidate) || !fs.existsSync(candidate)) continue;
        let declaredId: string;
        try {
          declaredId = loadFlow(candidate).id;
        } catch (err) {
          opts.onMiss?.({
            path: candidate,
            reason: "unparseable",
            error: err instanceof Error ? err.message : String(err),
          });
          continue;
        }
        if (declaredId === flowId) return candidate;
        opts.onMiss?.({ path: candidate, reason: "id-mismatch", declaredId });
      }
    }
    const entry = loadAllFlowEntries(root, { onWarn: () => {} }).find((e) => e.flow.id === flowId);
    if (entry) return entry.path;
  }
  return undefined;
}
